import {
  Box,
  Button,
  Checkbox,
  Divider,
  Flex,
  IconButton,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Text,
  VStack,
} from '@chakra-ui/react';
import { ReactComponent as FilterIcon } from 'assets/icons/filter.svg';
import { Code } from 'constants/keyboard';
import { TEMPLATES_FILTERED } from 'constants/mixpanel';
import { checkHasFiltersApplied } from 'hooks/usePaginatedTemplatesSearch/usePaginatedTemplatesSearch.helpers';
import { TemplateFilters } from 'hooks/usePaginatedTemplatesSearch/usePaginatedTemplatesSearch.types';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import cloneDeep from 'lodash.clonedeep';
import { KeyboardEvent, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { TEMPLATE_FILTER_CONFIG } from './TemplateFilter.config';
import { TemplateFilterConfig, TemplateFilterSectionConfig } from './TemplatesSearch.types';

interface TemplatesFilterInputProps {
  appliedFilters: TemplateFilters | null;
  applyFilters: (filters: TemplateFilters) => void;
}

export const TemplatesFilterInput = ({ appliedFilters, applyFilters }: TemplatesFilterInputProps) => {
  const { t } = useTranslation('editor_widget_menu', { useSuspense: false, keyPrefix: 'sideMenu.templatesMenu' });
  const hasFiltersApplied = useMemo(() => checkHasFiltersApplied(appliedFilters), [appliedFilters]);

  const clearAllFilters = useCallback(() => {
    applyFilters({});
  }, [applyFilters]);

  const stopEventPropagationHandler = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === Code.Escape) {
      e.stopPropagation();
    }
  }, []);

  return (
    <Box onKeyDown={stopEventPropagationHandler}>
      <Popover placement='bottom-end' isLazy>
        {({ isOpen }) => (
          <>
            <PopoverTrigger>
              <IconButton
                icon={<FilterIcon />}
                aria-label='Filter templates'
                variant='outline-with-active'
                size='sm'
                borderRadius='base'
                isActive={isOpen || hasFiltersApplied}
                data-testid='templates-filter-button'
                h='full'
                w={10}
              />
            </PopoverTrigger>
            <PopoverContent w='10.5rem' p={2} data-testid='filter-popover-content'>
              {TEMPLATE_FILTER_CONFIG.map((filterConfig) => (
                <FilterSection
                  key={filterConfig.sectionTitleKey}
                  filterConfig={filterConfig}
                  appliedFilters={appliedFilters}
                  applyFilters={applyFilters}
                />
              ))}
              <Button
                variant='outline'
                w='full'
                size='sm'
                borderRadius='base'
                data-testid='filter-clear-btn'
                onClick={clearAllFilters}
              >
                {t('filters.clearAll')}
              </Button>
            </PopoverContent>
          </>
        )}
      </Popover>
    </Box>
  );
};

interface FilterSectionProps {
  filterConfig: TemplateFilterSectionConfig;
  appliedFilters: TemplateFilters | null;
  applyFilters: (filters: TemplateFilters) => void;
}
/**
 * Renders filter section title + list of filters belonging to the section
 */
const FilterSection = ({ filterConfig, appliedFilters, applyFilters }: FilterSectionProps) => {
  const { t } = useTranslation('editor_widget_menu', { useSuspense: false, keyPrefix: 'sideMenu.templatesMenu' });
  const { sectionTitleKey, filters } = filterConfig;
  const translationKey = sectionTitleKey as unknown as TemplateStringsArray;

  return (
    <Flex direction='column' gap={2} fontSize='sm' as='fieldset'>
      <Text fontWeight='semibold' as='legend'>
        {t(translationKey)}
      </Text>
      <Divider />
      <VStack mb={4} align='flex-start'>
        {filters.map((filter) => (
          <FilterItem
            key={filter.labelKey}
            filterConfig={filter}
            appliedFilters={appliedFilters}
            applyFilters={applyFilters}
          />
        ))}
      </VStack>
    </Flex>
  );
};

interface FilterItemProps {
  filterConfig: TemplateFilterConfig;
  appliedFilters: TemplateFilters | null;
  applyFilters: (filters: TemplateFilters) => void;
}
/**
 * Renders a single filter item - including checkbox and label
 */
const FilterItem = ({ filterConfig, appliedFilters, applyFilters }: FilterItemProps) => {
  const { t } = useTranslation('editor_widget_menu', { useSuspense: false, keyPrefix: 'sideMenu.templatesMenu' });
  const { labelKey, checkIsApplied, toggleFilter, mpFilterName } = filterConfig;
  const translationKey = labelKey as unknown as TemplateStringsArray;

  const isChecked = useMemo(() => !!appliedFilters && checkIsApplied(appliedFilters), [appliedFilters, checkIsApplied]);
  const handleSelect = useCallback(() => {
    // Trigger MP event only when filter is applied
    if (!isChecked) {
      Mixpanel.track(TEMPLATES_FILTERED.eventName, {
        filter_name: mpFilterName,
      });
    }

    const newFilters = cloneDeep(appliedFilters) || {};
    toggleFilter(newFilters);
    applyFilters(newFilters);
  }, [appliedFilters, applyFilters, toggleFilter, isChecked, mpFilterName]);

  return (
    <Checkbox size='sm' isChecked={isChecked} onChange={handleSelect} data-testid={`filter-checkbox-${labelKey}`}>
      {t(translationKey)}
    </Checkbox>
  );
};
